import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject, signal } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import {
  QUESTIONNAIRE_DEFAULT_BRANDING,
  QUESTIONNAIRE_DEFAULT_EMAILS,
  QUESTIONNAIRE_DEFAULT_SETTINGS,
  QUESTIONNAIRE_SAMPLE_PAGES,
} from '@common/data';
import type {
  Questionnaire,
  QuestionnaireBranding,
  QuestionnaireBuilderPage,
  QuestionnaireDetail,
  QuestionnaireDraftSnapshot,
  QuestionnaireElement,
  QuestionnaireElementDefinition,
  QuestionnaireEmailKind,
  QuestionnaireEmailTemplate,
  QuestionnairePageKind,
  QuestionnaireSettings,
} from '@common/types';

import { GrToastService } from './toast.service';
import { QuestionnairesService } from './questionnaires.service';

/** Route id for a questionnaire that has never been saved: /account/questionnaires/new. */
export const NEW_QUESTIONNAIRE_ID = 'new';

const DRAFT_STORAGE_KEY = 'gr.questionnaire-drafts';

@Injectable({ providedIn: 'root' })
export class QuestionnaireBuilderService {
  private readonly questionnaires = inject(QuestionnairesService);
  private readonly toasts = inject(GrToastService);
  private readonly document = inject(DOCUMENT);

  private readonly details = signal<Record<string, QuestionnaireDetail>>({});
  private readonly dirtyIds = signal<ReadonlyArray<string>>([]);
  private readonly seq = signal<number>(0);
  private warnedAboutStorage = false;

  constructor() {
    this.restore();

    // Every edit lands in session storage, so a reload — deliberate or not — puts you
    // back on the same questionnaire with the same unsaved work.
    effect(() => {
      const snapshot: QuestionnaireDraftSnapshot = {
        details: this.details(),
        dirtyIds: this.dirtyIds(),
        seq: this.seq(),
      };
      try {
        this.document.defaultView?.sessionStorage.setItem(
          DRAFT_STORAGE_KEY,
          JSON.stringify(snapshot),
        );
        this.warnedAboutStorage = false;
      } catch {
        // Almost always the storage quota, and almost always a large uploaded image.
        // Editing carries on in memory — but say so, because a reload would lose it.
        if (this.warnedAboutStorage) return;
        this.warnedAboutStorage = true;
        this.toasts.warning(
          'This draft is too large to keep for a reload — save your changes to be safe.',
          'Draft not stored',
        );
      }
    });
  }

  // Pure read: safe inside computed()/effect(). Hydration is ensureDetail's job —
  // writing the cache from here would throw NG0600 when a computed reads a cold id.
  detail(id: string): QuestionnaireDetail | undefined {
    return this.details()[id];
  }

  /** True once the draft holds edits that are not in the questionnaire itself yet. */
  isDirty(id: string): boolean {
    return this.dirtyIds().includes(id);
  }

  ensureDetail(id: string): QuestionnaireDetail | undefined {
    const existing = this.details()[id];
    if (existing) return existing;

    const created =
      id === NEW_QUESTIONNAIRE_ID ? this.buildNewDetail() : this.detailFromSummary(id);
    if (!created) return undefined;

    this.details.update((all) => ({ ...all, [id]: created }));
    return created;
  }

  /**
   * Commits the draft. A new questionnaire is created first, then its draft moves
   * across to the real id so the editor can redirect to it without losing anything.
   */
  save(id: string): Observable<Questionnaire> {
    const draft = this.details()[id];
    if (!draft) return throwError(() => new Error('There is nothing to save.'));

    if (id === NEW_QUESTIONNAIRE_ID) {
      return this.questionnaires
        .create(draft.name, draft.type)
        .pipe(map((created) => this.adopt(id, created, draft)));
    }

    return this.questionnaires
      .saveSummary(id, {
        name: draft.name,
        title: draft.settings.title,
        type: draft.type,
        pageCount: draft.pages.length,
      })
      .pipe(tap(() => this.markClean(id)));
  }

  /** Drops the draft; the next read rebuilds it from the saved questionnaire. */
  discard(id: string): void {
    this.details.update((all) => {
      const next = { ...all };
      delete next[id];
      return next;
    });
    this.markClean(id);
  }

  addPage(id: string): void {
    this.mutate(id, (detail) => {
      const normalPages = detail.pages.filter((page) => page.kind === 'normal').length;
      const page: QuestionnaireBuilderPage = {
        id: this.nextId('page'),
        name: `Page ${normalPages + 1}`,
        kind: 'normal',
        elements: [],
      };
      const thankYouIndex = detail.pages.findIndex((item) => item.kind === 'thank-you');
      const pages =
        thankYouIndex === -1
          ? [...detail.pages, page]
          : [
              ...detail.pages.slice(0, thankYouIndex),
              page,
              ...detail.pages.slice(thankYouIndex),
            ];
      return { ...detail, pages };
    });
  }

  removePage(id: string, pageId: string): void {
    this.mutate(id, (detail) => ({
      ...detail,
      pages: detail.pages.filter((page) => page.id !== pageId),
      logic: detail.logic.filter(
        (rule) => rule.sourcePageId !== pageId && rule.targetPageId !== pageId,
      ),
    }));
  }

  renamePage(id: string, pageId: string, name: string): void {
    this.patchPage(id, pageId, (page) => ({ ...page, name }));
  }

  setPageKind(id: string, pageId: string, kind: QuestionnairePageKind): void {
    this.mutate(id, (detail) => ({
      ...detail,
      pages: detail.pages.map((page) => {
        if (page.id === pageId) return { ...page, kind };
        if (kind === 'thank-you' && page.kind === 'thank-you') {
          return { ...page, kind: 'normal' as const };
        }
        return page;
      }),
    }));
  }

  movePage(id: string, fromIndex: number, toIndex: number): void {
    this.mutate(id, (detail) => ({ ...detail, pages: this.reorder(detail.pages, fromIndex, toIndex) }));
  }

  duplicatePage(id: string, pageId: string): void {
    this.mutate(id, (detail) => {
      const index = detail.pages.findIndex((page) => page.id === pageId);
      if (index === -1) return detail;
      const source = detail.pages[index];
      const copy: QuestionnaireBuilderPage = {
        ...source,
        id: this.nextId('page'),
        name: `${source.name} (Copy)`,
        kind: 'normal',
        elements: source.elements.map((element) => ({ ...element, id: this.nextId('el') })),
      };
      return {
        ...detail,
        pages: [...detail.pages.slice(0, index + 1), copy, ...detail.pages.slice(index + 1)],
      };
    });
  }

  addElementAt(
    id: string,
    pageId: string,
    definition: QuestionnaireElementDefinition,
    index: number,
  ): void {
    this.patchPage(id, pageId, (page) => {
      const elements = page.elements.slice();
      elements.splice(index, 0, this.createElement(definition));
      return { ...page, elements };
    });
  }

  mergePageInto(id: string, sourcePageId: string, targetPageId: string): void {
    this.mutate(id, (detail) => {
      const source = detail.pages.find((page) => page.id === sourcePageId);
      if (!source) return detail;
      return {
        ...detail,
        pages: detail.pages
          .filter((page) => page.id !== sourcePageId)
          .map((page) =>
            page.id === targetPageId
              ? { ...page, elements: [...page.elements, ...source.elements] }
              : page,
          ),
      };
    });
  }

  addElement(id: string, pageId: string, definition: QuestionnaireElementDefinition): void {
    this.patchPage(id, pageId, (page) => ({
      ...page,
      elements: [...page.elements, this.createElement(definition)],
    }));
  }

  updateElement(
    id: string,
    pageId: string,
    elementId: string,
    change: Partial<QuestionnaireElement>,
  ): void {
    this.patchPage(id, pageId, (page) => ({
      ...page,
      elements: page.elements.map((element) =>
        element.id === elementId ? { ...element, ...change } : element,
      ),
    }));
  }

  removeElement(id: string, pageId: string, elementId: string): void {
    this.mutate(id, (detail) => ({
      ...detail,
      pages: detail.pages.map((page) =>
        page.id === pageId
          ? { ...page, elements: page.elements.filter((element) => element.id !== elementId) }
          : page,
      ),
      logic: detail.logic.filter((rule) => rule.sourceElementId !== elementId),
    }));
  }

  duplicateElement(id: string, pageId: string, elementId: string): void {
    this.patchPage(id, pageId, (page) => {
      const index = page.elements.findIndex((element) => element.id === elementId);
      if (index === -1) return page;
      const source = page.elements[index];
      const copy: QuestionnaireElement = { ...source, id: this.nextId('el') };
      return {
        ...page,
        elements: [...page.elements.slice(0, index + 1), copy, ...page.elements.slice(index + 1)],
      };
    });
  }

  moveElement(id: string, pageId: string, fromIndex: number, toIndex: number): void {
    this.patchPage(id, pageId, (page) => ({
      ...page,
      elements: this.reorder(page.elements, fromIndex, toIndex),
    }));
  }

  moveElementBetweenPages(
    id: string,
    fromPageId: string,
    toPageId: string,
    fromIndex: number,
    toIndex: number,
  ): void {
    this.mutate(id, (detail) => {
      const source = detail.pages.find((page) => page.id === fromPageId);
      const moved = source?.elements[fromIndex];
      if (!moved) return detail;
      return {
        ...detail,
        pages: detail.pages.map((page) => {
          if (page.id === fromPageId) {
            return { ...page, elements: page.elements.filter((_, i) => i !== fromIndex) };
          }
          if (page.id === toPageId) {
            const next = page.elements.slice();
            next.splice(toIndex, 0, moved);
            return { ...page, elements: next };
          }
          return page;
        }),
      };
    });
  }

  updateBranding(id: string, change: Partial<QuestionnaireBranding>): void {
    this.mutate(id, (detail) => ({ ...detail, branding: { ...detail.branding, ...change } }));
  }

  updateSettings(id: string, change: Partial<QuestionnaireSettings>): void {
    this.mutate(id, (detail) => ({ ...detail, settings: { ...detail.settings, ...change } }));
  }

  updateEmail(
    id: string,
    kind: QuestionnaireEmailKind,
    language: string,
    change: Partial<QuestionnaireEmailTemplate>,
  ): void {
    this.mutate(id, (detail) => {
      const exists = detail.emails.some(
        (email) => email.kind === kind && email.language === language,
      );
      const emails = exists
        ? detail.emails.map((email) =>
            email.kind === kind && email.language === language ? { ...email, ...change } : email,
          )
        : [
            ...detail.emails,
            {
              kind,
              language,
              subject: '',
              senderName: '',
              freestyle: false,
              content: '',
              ...change,
            },
          ];
      return { ...detail, emails };
    });
  }

  private createElement(definition: QuestionnaireElementDefinition): QuestionnaireElement {
    const base: QuestionnaireElement = {
      id: this.nextId('el'),
      kind: definition.kind,
      label: definition.group === 'question' ? 'Untitled question' : definition.label,
      helpText: '',
      required: false,
      choices: [],
      rows: [],
      placeholder: '',
      content: '',
      headlineLevel: definition.headlineLevel ?? 2,
      imageUrl: '',
      imageAlt: '',
      parameterKey: '',
    };

    switch (definition.kind) {
      case 'radio':
      case 'dropdown':
      case 'checkbox':
      case 'multi-select':
        return { ...base, choices: this.defaultChoices(3) };
      case 'radio-grid':
      case 'dropdown-grid':
      case 'checkbox-grid':
        return { ...base, choices: this.defaultChoices(3), rows: this.defaultRows(2) };
      case 'textbox-grid':
        return { ...base, rows: this.defaultRows(2) };
      case 'headline':
        return { ...base, label: 'Your headline' };
      case 'text':
        return { ...base, label: 'Text block', content: 'Add your copy here.' };
      case 'html':
        return { ...base, label: 'HTML block', content: '<p>Custom markup</p>' };
      case 'horizontal-rule':
        return { ...base, label: 'Divider' };
      default:
        return base;
    }
  }

  private defaultChoices(count: number) {
    return Array.from({ length: count }, (_, index) => ({
      id: this.nextId('choice'),
      label: `Option ${index + 1}`,
      value: `option-${index + 1}`,
    }));
  }

  private defaultRows(count: number) {
    return Array.from({ length: count }, (_, index) => ({
      id: this.nextId('row'),
      label: `Row ${index + 1}`,
      value: `row-${index + 1}`,
    }));
  }

  private detailFromSummary(id: string): QuestionnaireDetail | undefined {
    const summary = this.questionnaires.getById(id);
    return summary ? this.buildDetail(summary) : undefined;
  }

  private buildNewDetail(): QuestionnaireDetail {
    return this.buildDetail({
      id: NEW_QUESTIONNAIRE_ID,
      name: 'New questionnaire',
      title: '',
      type: 'post-stay',
      status: 'draft',
      isDefault: false,
      pageCount: QUESTIONNAIRE_SAMPLE_PAGES.length,
      createdAtIso: new Date().toISOString(),
    });
  }

  private buildDetail(summary: Questionnaire): QuestionnaireDetail {
    return {
      ...summary,
      pages: QUESTIONNAIRE_SAMPLE_PAGES.map((page) => ({
        ...page,
        id: `${summary.id}-${page.id}`,
        elements: page.elements.map((element) => ({
          ...element,
          id: `${summary.id}-${element.id}`,
        })),
      })),
      branding: { ...QUESTIONNAIRE_DEFAULT_BRANDING },
      settings: {
        ...QUESTIONNAIRE_DEFAULT_SETTINGS,
        title: summary.title,
        description: summary.name,
      },
      emails: QUESTIONNAIRE_DEFAULT_EMAILS.map((email) => ({ ...email })),
      logic: [],
    };
  }

  // Re-keys the unsaved draft onto the id the questionnaire was just given, so the
  // pages and branding built up before saving survive the redirect.
  private adopt(
    draftId: string,
    created: Questionnaire,
    draft: QuestionnaireDetail,
  ): Questionnaire {
    this.questionnaires.syncPageCount(created.id, draft.pages.length);
    const summary = this.questionnaires.getById(created.id) ?? created;

    this.details.update((all) => {
      const next = { ...all, [created.id]: { ...draft, ...summary } };
      delete next[draftId];
      return next;
    });
    this.dirtyIds.update((ids) => ids.filter((id) => id !== draftId && id !== created.id));

    return summary;
  }

  private markDirty(id: string): void {
    if (this.dirtyIds().includes(id)) return;
    this.dirtyIds.update((ids) => [...ids, id]);
  }

  private markClean(id: string): void {
    this.dirtyIds.update((ids) => ids.filter((item) => item !== id));
  }

  private restore(): void {
    try {
      const raw = this.document.defaultView?.sessionStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) return;
      const snapshot = JSON.parse(raw) as Partial<QuestionnaireDraftSnapshot>;
      if (!snapshot.details) return;
      this.details.set(snapshot.details);
      this.dirtyIds.set(snapshot.dirtyIds ?? []);
      this.seq.set(snapshot.seq ?? 0);
    } catch {
      // A corrupt snapshot is not worth failing the editor over — start clean.
    }
  }

  private patchPage(
    id: string,
    pageId: string,
    change: (page: QuestionnaireBuilderPage) => QuestionnaireBuilderPage,
  ): void {
    this.mutate(id, (detail) => ({
      ...detail,
      pages: detail.pages.map((page) => (page.id === pageId ? change(page) : page)),
    }));
  }

  // Edits stay in the draft — nothing reaches the questionnaire itself until save().
  private mutate(id: string, change: (detail: QuestionnaireDetail) => QuestionnaireDetail): void {
    const current = this.ensureDetail(id);
    if (!current) return;
    const next = change(current);
    this.details.update((all) => ({ ...all, [id]: next }));
    this.markDirty(id);
  }

  private reorder<T>(items: ReadonlyArray<T>, fromIndex: number, toIndex: number): T[] {
    const next = items.slice();
    const [moved] = next.splice(fromIndex, 1);
    if (moved === undefined) return next;
    next.splice(toIndex, 0, moved);
    return next;
  }

  // Signal-backed so the counter is part of the persisted snapshot: after a reload the
  // next generated id carries on rather than colliding with a restored one.
  private nextId(prefix: string): string {
    const next = this.seq() + 1;
    this.seq.set(next);
    return `${prefix}-${next}`;
  }
}
